import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SCORE_LEVEL_LABEL_KEY, scoreLevel, type TRisk } from "@/zapi-frontend/shared/types/risks";

const MONO_LABEL = "font-robotoMono text-[12px] uppercase tracking-[1.2px] text-innoveraZinc-600 whitespace-nowrap";
const TICK_LABEL = "text-[12px] leading-[18px] text-innoveraZinc-500 whitespace-nowrap";
const VERTICAL_TEXT = "[writing-mode:vertical-rl] rotate-180";

const PLOT_INSET_PERCENT = 4;
const position = (score: number) => `${PLOT_INSET_PERCENT + ((score - 1) / 9) * (100 - 2 * PLOT_INSET_PERCENT)}%`;
const FADED_BORDER = "border-[rgba(24,24,27,0.3)]";

type TLabelSide = "left" | "right" | "top" | "bottom";
type TBox = { left: number; top: number; right: number; bottom: number };

const LABEL_SIDES: TLabelSide[] = ["left", "right", "top", "bottom"];
const LABEL_GAP = 10;
const LABEL_POSITION: Record<TLabelSide, string> = {
  left: "right-full top-1/2 mr-2.5 -translate-y-1/2",
  right: "left-full top-1/2 ml-2.5 -translate-y-1/2",
  top: "bottom-full left-1/2 mb-2.5 -translate-x-1/2",
  bottom: "top-full left-1/2 mt-2.5 -translate-x-1/2"
};

const labelBox = (side: TLabelSide, dot: TBox, width: number, height: number): TBox => {
  const centerX = (dot.left + dot.right) / 2;
  const centerY = (dot.top + dot.bottom) / 2;
  const left =
    side === "left" ? dot.left - LABEL_GAP - width : side === "right" ? dot.right + LABEL_GAP : centerX - width / 2;
  const top =
    side === "top" ? dot.top - LABEL_GAP - height : side === "bottom" ? dot.bottom + LABEL_GAP : centerY - height / 2;
  return { left, top, right: left + width, bottom: top + height };
};

const QUADRANT_NAMES = [
  { labelKey: "risks.quadrant.pinDown", className: "left-5 top-5" },
  { labelKey: "risks.quadrant.crux", className: "right-5 top-5 text-innoveraOrange-400" },
  { labelKey: "risks.quadrant.noted", className: "bottom-5 left-5" },
  { labelKey: "risks.quadrant.monitor", className: "bottom-5 right-5" }
] as const;

const overlaps = (a: TBox, b: TBox) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

type TRiskDotProps = {
  risk: TRisk;
  isCrux: boolean;
  isSelected: boolean;
  isDimmed: boolean;
  labelSide: TLabelSide | null | undefined;
  onSelectedChange: (selected: boolean) => void;
  onAddress: (risk: TRisk) => void;
};

function RiskField({
  label,
  value,
  valueVariant = "body-2"
}: {
  label: string;
  value: string;
  valueVariant?: "body-2" | "label-2";
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex h-5 items-center">
        <FigmaTypography variant="label-3" color="zinc500">
          {label}
        </FigmaTypography>
      </div>
      <div className={cn("flex items-center py-1", valueVariant === "label-2" && "h-[22px]")}>
        <FigmaTypography variant={valueVariant} color="zinc900">
          {value}
        </FigmaTypography>
      </div>
    </div>
  );
}

function RiskDot({ risk, isCrux, isSelected, isDimmed, labelSide, onSelectedChange, onAddress }: TRiskDotProps) {
  const { t } = useTranslation("projectHome");
  const isAddressed = risk.status === "addressed";
  const inCruxQuadrant = risk.impact >= 6 && risk.uncertainty >= 6;
  const isOpenInCruxQuadrant = inCruxQuadrant && !isAddressed;
  const showLabel = isCrux || isOpenInCruxQuadrant;

  return (
    <div
      className={cn(
        "absolute -translate-x-1/2 translate-y-1/2 transition-opacity",
        isSelected ? "z-20" : "z-10",
        isDimmed && "opacity-40"
      )}
      style={{ left: position(risk.uncertainty), bottom: position(risk.impact) }}
    >
      <Popover open={isSelected} onOpenChange={onSelectedChange}>
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label={risk.title}
              data-testid={`risk-dot-${risk.id}`}
              className="group relative flex items-center justify-center outline-hidden"
            />
          }
        >
          <span
            data-graph-obstacle
            data-dot-for={risk.id}
            className={cn(
              "block rounded-full border-2 transition-transform group-hover:scale-110",
              isCrux ? "size-6" : isOpenInCruxQuadrant ? "size-4" : "size-2",
              isCrux && !isAddressed ? "bg-innoveraOrange-400" : "bg-white",
              isSelected ? FADED_BORDER : isAddressed ? "border-innoveraZinc-300" : "border-innoveraZinc-900"
            )}
          />
          {showLabel && (
            <span
              data-label-for={risk.id}
              className={cn(
                "absolute whitespace-nowrap text-[16px] font-medium leading-[15px] text-black",
                LABEL_POSITION[labelSide ?? "left"],
                labelSide === null && "invisible group-hover:visible"
              )}
            >
              {risk.title}
            </span>
          )}
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={10}
          alignOffset={-6}
          className="w-[320px] gap-0 rounded-none p-5 shadow-[0px_7px_6px_0px_rgba(24,24,27,0.03)]"
        >
          <div className="flex flex-col items-end gap-4">
            <div className="flex min-h-8 w-full items-center">
              <FigmaTypography variant="heading-3" color="zinc900">
                {risk.title}
              </FigmaTypography>
            </div>

            <div className="flex w-full flex-col gap-4">
              <RiskField label={t("risks.fieldWhatMustHoldTrue")} value={risk.whatMustHoldTrue} />
              <RiskField label={t("risks.fieldWhyItMatters")} value={risk.whyItMatters} />
              <div className="flex gap-4">
                <RiskField
                  label={t("risks.columnImpact")}
                  value={t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.impact)])}
                  valueVariant="label-2"
                />
                <RiskField
                  label={t("risks.columnUncertainty")}
                  value={t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.uncertainty)])}
                  valueVariant="label-2"
                />
              </div>
              <div className="h-px w-full bg-innoveraZinc-150" />
            </div>

            <Button
              type="button"
              onClick={() => {
                onSelectedChange(false);
                onAddress(risk);
              }}
              data-testid={`risk-dot-address-${risk.id}`}
            >
              {isAddressed ? t("risks.addressed") : t("risks.addressRisk")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

type TRiskGraphProps = {
  risks: TRisk[];
  cruxIds: Set<number>;
  onAddress: (risk: TRisk) => void;
};

export function RiskGraph({ risks, cruxIds, onAddress }: TRiskGraphProps) {
  const { t } = useTranslation("projectHome");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  // null: no side has room, so the label only shows on hover.
  const [labelSides, setLabelSides] = useState<Record<number, TLabelSide | null>>({});
  const [hiddenQuadrantNames, setHiddenQuadrantNames] = useState<Set<string>>(new Set());

  useLayoutEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;

    const placeLabels = () => {
      const origin = plot.getBoundingClientRect();
      const toBox = (element: Element): TBox => {
        const rect = element.getBoundingClientRect();
        return {
          left: rect.left - origin.left,
          top: rect.top - origin.top,
          right: rect.right - origin.left,
          bottom: rect.bottom - origin.top
        };
      };
      const fitsPlot = (box: TBox) =>
        box.left >= 0 && box.top >= 0 && box.right <= origin.width && box.bottom <= origin.height;

      const taken = Array.from(plot.querySelectorAll("[data-graph-obstacle]"), toBox);
      const sides: Record<number, TLabelSide | null> = {};
      const cruxFirst = [...risks].sort((a, b) => Number(cruxIds.has(b.id)) - Number(cruxIds.has(a.id)));
      for (const risk of cruxFirst) {
        const label = plot.querySelector<HTMLElement>(`[data-label-for="${risk.id}"]`);
        const dot = plot.querySelector(`[data-dot-for="${risk.id}"]`);
        if (!label || !dot) continue;
        const dotBox = toBox(dot);
        const placed = LABEL_SIDES.map((side) => ({
          side,
          box: labelBox(side, dotBox, label.offsetWidth, label.offsetHeight)
        })).find(({ box }) => fitsPlot(box) && !taken.some((other) => overlaps(box, other)));
        sides[risk.id] = placed?.side ?? null;
        if (placed) taken.push(placed.box);
      }
      setLabelSides(sides);

      const coveredNames = Array.from(plot.querySelectorAll("[data-quadrant-name]"))
        .filter((name) => taken.some((box) => overlaps(toBox(name), box)))
        .map((name) => name.getAttribute("data-quadrant-name") ?? "");
      setHiddenQuadrantNames(new Set(coveredNames));
    };

    placeLabels();
    const observer = new ResizeObserver(placeLabels);
    observer.observe(plot);
    return () => observer.disconnect();
  }, [risks, cruxIds]);

  return (
    <div className="relative h-full min-h-[480px] pb-14 pl-14 pr-2 pt-2">
      <div ref={plotRef} className="relative size-full border border-innoveraZinc-150">
        <div className="absolute right-0 top-0 h-1/2 w-1/2 bg-innoveraOrange-400 opacity-10" />

        <div className="absolute inset-y-0 left-1/4 w-px bg-innoveraZinc-150" />
        <div className="absolute inset-y-0 left-3/4 w-px bg-innoveraZinc-150" />
        <div className="absolute inset-x-0 top-1/4 h-px bg-innoveraZinc-150" />
        <div className="absolute inset-x-0 top-3/4 h-px bg-innoveraZinc-150" />
        <div className="absolute inset-y-0 left-1/2 w-px bg-[repeating-linear-gradient(to_bottom,var(--innovera-zinc300)_0_6px,transparent_6px_12px)]" />
        <div className="absolute inset-x-0 top-1/2 h-px bg-[repeating-linear-gradient(to_right,var(--innovera-zinc300)_0_6px,transparent_6px_12px)]" />

        {QUADRANT_NAMES.map(({ labelKey, className }) => (
          <span
            key={labelKey}
            data-quadrant-name={labelKey}
            className={cn(MONO_LABEL, "absolute", className, hiddenQuadrantNames.has(labelKey) && "invisible")}
          >
            {t(labelKey)}
          </span>
        ))}

        {risks.map((risk) => (
          <RiskDot
            key={risk.id}
            risk={risk}
            isCrux={cruxIds.has(risk.id)}
            isSelected={selectedId === risk.id}
            isDimmed={selectedId !== null && selectedId !== risk.id}
            labelSide={labelSides[risk.id]}
            onSelectedChange={(selected) => setSelectedId(selected ? risk.id : null)}
            onAddress={onAddress}
          />
        ))}
      </div>

      <div className="absolute bottom-14 left-2 top-2 flex w-4 items-center justify-center">
        <span className={cn(MONO_LABEL, VERTICAL_TEXT)}>{t("risks.axisImpact")}</span>
      </div>
      <div className="absolute bottom-14 left-[30px] top-2 flex w-[18px] flex-col items-center justify-between">
        <span className={cn(TICK_LABEL, VERTICAL_TEXT)}>{t("risks.level.high")}</span>
        <span className={cn(TICK_LABEL, VERTICAL_TEXT)}>{t("risks.level.medium")}</span>
        <span className={cn(TICK_LABEL, VERTICAL_TEXT)}>{t("risks.level.low")}</span>
      </div>

      <div className="absolute bottom-[30px] left-14 right-2 flex justify-between">
        <span className={TICK_LABEL}>{t("risks.level.low")}</span>
        <span className={TICK_LABEL}>{t("risks.level.medium")}</span>
        <span className={TICK_LABEL}>{t("risks.level.high")}</span>
      </div>
      <div className="absolute bottom-2 left-14 right-2 flex justify-center">
        <span className={MONO_LABEL}>{t("risks.axisUncertainty")}</span>
      </div>
    </div>
  );
}
